import { FormControl, NG_ASYNC_VALIDATORS, ValidationErrors } from '@angular/forms';
import { Directive, EventEmitter, HostBinding, Input, OnDestroy, Output } from '@angular/core';
import { catchError, finalize, map, Observable, of, Subscription, switchMap, timer } from 'rxjs';
import { ExternalLinkService } from '@reg/shared/service';
import { ExternalSourceType } from '@reg/shared/model';

@Directive({
    selector: '[floAsyncExternalLinkValidator]',
    providers: [
        { provide: NG_ASYNC_VALIDATORS, useExisting: ExternalLinkCheckDirective, multi: true },
    ],
})
export class ExternalLinkCheckDirective implements OnDestroy {
    @Output() public isValidatingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public existsChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input() public eventId: string;
    @Input() public eventStartDateTime: Date;
    @Input() public externalSource: ExternalSourceType;

    private _isValidating: boolean = false;
    @HostBinding('class.is-validating')
    set isValidating(isValidating: boolean) {
        this._isValidating = isValidating;
        this.isValidatingChange.emit(isValidating);
    }

    get isValidating(): boolean {
        return this._isValidating;
    }

    private formControl: FormControl;
    private defaultDelay: number = 600; // wait 600 milliseconds before validating so we do not validate on every key stroke
    private delay: number = this.defaultDelay;
    private valueChangedSubscription: Subscription;

    constructor(private externalLinkService: ExternalLinkService) {
    }

    public validate(control: FormControl): Observable<ValidationErrors> {
        this.isValidating = true;

        if (!this.formControl) {
            this.formControl = control;

            // First time loading so lets validate immediately
            this.delay = 0;
        }

        if (!control.value) {
            this.existsChange.emit(false);
            this.isValidating = false;
            return of(null);
        }

        // This timer will be auto canceled if a new value comes in before making the service call
        // because the control will unsubscribe from the previous validation check
        return timer(this.delay).pipe(switchMap(() => {
            this.delay = this.defaultDelay;

            return this.externalLinkService.isValid(
                this.eventId,
                this.externalSource,
                control.value,
                this.eventStartDateTime,
            )
                .pipe(
                    map((isValid: boolean) => {
                        if (!control.value) {
                            this.existsChange.emit(null);
                            return null;
                        }

                        this.existsChange.emit(isValid);

                        return isValid ? null : { invalid: true };
                    }),
                    finalize(() => {
                        this.isValidating = false;
                    }),
                    catchError(() => {
                        this.existsChange.emit(false);
                        return of({ invalid: true });
                    }),
                );
        }));
    }

    public ngOnDestroy(): void {
        if (this.valueChangedSubscription) {
            this.valueChangedSubscription.unsubscribe();
        }
    }
}
